/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import type { DocNode } from '@atlaskit/adf-schema';
import { ButtonGroup } from '@atlaskit/button';
import Button, { IconButton } from '@atlaskit/button/new';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import HistoryIcon from '@atlaskit/icon-lab/core/history';
import SuggestedEditIcon from '@atlaskit/icon-lab/core/suggested-edit';
import CrossIcon from '@atlaskit/icon/core/cross';
import LayoutTwoColumnsIcon from '@atlaskit/icon/core/layout-two-columns';
import Lozenge from '@atlaskit/lozenge';
import Modal from '@atlaskit/modal-dialog';
import { Box, Flex, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { AiSuggestionsPlugin } from '../../aiSuggestionsPluginType';
import { deriveHost, fetchStreaming } from '../utils/streaming';

import { EditorPreview } from './EditorPreview/EditorPreview';
import { usePreviewFullPagePreset } from './EditorPreview/usePreviewFullPagePreset';
export type ViewMode = 'view1' | 'view2';

const styles = cssMap({
	hidden: {
		display: 'none',
	},
	root: {
		flex: 1,
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
	},
	headerContainer: {
		width: '100%',
		flexShrink: 0,
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.300'),
	},
	columnsContainer: {
		display: 'flex',
		flex: 1,
		minHeight: 0,
		gap: token('space.200'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	column: {
		flex: 1,
		overflow: 'auto',
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.xlarge'),
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
	},
	viewSwitcherContainer: {
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.100'),
	},
});
const globalStylesCompiled = `
	.atlaskit-portal [data-testid="suggestions-staging-area-modal"] {
		border-radius: 16px;
        background-color: ${token('elevation.surface.sunken')};
	}
`;

const globalStyles = () => (
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles
	<style
		data-testid="global-styles"
		type="text/css"
		// eslint-disable-next-line react/no-danger
		dangerouslySetInnerHTML={{ __html: globalStylesCompiled }}
	/>
);

type ToolCall = {
	arguments: {
		localId: string;
		value: string;
	};
	name: string;
	reasoning: string;
};

export type Suggestion = {
	description: string;
	tool_calls: ToolCall[];
	type: string;
};

/**
 * The StagingArea component displays a side-by-side view of original and suggested content.
 * It allows users to review AI-generated suggestions and apply them to the editor.
 * View1: Original | Suggested. View2: Single EditorPreview | List of suggestion cards with reasoning.
 */
export function StagingArea({
	adf,
	suggestions,
	providerFactory,
	api: _api,
	onClose,
	onFetchSuggestions,
	isFetching,
}: {
	adf: DocNode;
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	isFetching?: boolean;
	onClose: () => void;
	onFetchSuggestions?: () => void;
	providerFactory?: ProviderFactory;

	suggestions: Suggestion[];
}) {
	const { formatMessage } = useIntl();
	const { preset: originalPreviewPreset, editorApi: originalPreviewEditorApi } =
		usePreviewFullPagePreset({
			providerFactory,
			isOriginalPreview: true,
		});
	const { preset: suggestionsPreviewPreset, editorApi: suggestionsPreviewEditorApi } =
		usePreviewFullPagePreset({ providerFactory });

	const [viewMode, setViewMode] = useState<ViewMode>('view1');

	const fetchButtonLabel = isFetching
		? formatMessage(aiSuggestionsMessages.fetchSuggestionsButtonLoadingLabel)
		: formatMessage(aiSuggestionsMessages.fetchSuggestionsButtonLabel);

	const toggleShowDiff = useCallback(() => {
		suggestionsPreviewEditorApi?.core.actions.execute(
			suggestionsPreviewEditorApi?.trackChanges?.commands?.showDiff,
		);
		suggestionsPreviewEditorApi?.core.actions.execute(
			suggestionsPreviewEditorApi?.trackChanges?.commands?.toggleChanges,
		);
	}, [suggestionsPreviewEditorApi]);
	return (
		<Flex direction="column" xcss={styles.root}>
			<Flex alignItems="center" justifyContent="space-between" xcss={styles.headerContainer}>
				<Inline space="space.100" alignBlock="center">
					<Text weight="bold">{formatMessage(aiSuggestionsMessages.previewLabel)}</Text>
				</Inline>
				<Box xcss={styles.viewSwitcherContainer}>
					<Inline space="space.100" alignBlock="center">
						<Text size="small" weight="medium">
							{formatMessage(aiSuggestionsMessages.viewLabel)}
						</Text>
						<ButtonGroup>
							<IconButton
								icon={SuggestedEditIcon}
								label="view1"
								appearance="subtle"
								isSelected={viewMode === 'view1'}
								spacing="compact"
								onClick={() => setViewMode('view1')}
							/>
							<IconButton
								icon={LayoutTwoColumnsIcon}
								label="view2"
								appearance="subtle"
								isSelected={viewMode === 'view2'}
								spacing="compact"
								onClick={() => setViewMode('view2')}
							/>
						</ButtonGroup>
					</Inline>
				</Box>
				<Inline space="space.100">
					{onFetchSuggestions && (
						<Button appearance="default" onClick={onFetchSuggestions} isDisabled={isFetching}>
							{fetchButtonLabel}
						</Button>
					)}
					<IconButton
						icon={HistoryIcon}
						label="diff"
						appearance="default"
						onClick={toggleShowDiff}
					/>

					<Button appearance="primary" isDisabled>
						{formatMessage(aiSuggestionsMessages.applyChangesButtonLabel)}
					</Button>
					<IconButton icon={CrossIcon} label="close" appearance="default" onClick={onClose} />
				</Inline>
			</Flex>
			<div css={styles.columnsContainer}>
				<div css={[styles.column, viewMode === 'view1' && styles.hidden]}>
					<Lozenge appearance="neutral">
						{formatMessage(aiSuggestionsMessages.originalViewLabel)}
					</Lozenge>
					<EditorPreview
						adf={adf}
						preset={originalPreviewPreset}
						editorApi={originalPreviewEditorApi}
					/>
				</div>
				<div css={styles.column}>
					{viewMode === 'view2' && (
						<Lozenge appearance="discovery">
							{formatMessage(aiSuggestionsMessages.suggestedLabel)}
						</Lozenge>
					)}
					<EditorPreview
						adf={adf}
						suggestions={suggestions}
						preset={suggestionsPreviewPreset}
						editorApi={suggestionsPreviewEditorApi}
					/>
				</div>
			</div>
		</Flex>
	);
}

/**
 * Modal wrapper for the StagingArea component.
 * Displays the staging area in a full-screen modal for reviewing AI suggestions.
 * Owns suggestions state and fetch logic; "Fetch suggestions" is shown next to Apply changes.
 */
export function StagingAreaModal({
	adf,
	api,
	onClose,
	initialSuggestions = [],
	providerFactory,
}: {
	adf: DocNode;
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	initialSuggestions?: Suggestion[];
	onClose: () => void;
	providerFactory?: ProviderFactory;
}) {
	const [suggestions, setSuggestions] = useState<Suggestion[]>(initialSuggestions);
	const [isFetching, setIsFetching] = useState(false);

	const onFetchSuggestions = useCallback(async () => {
		const abortController = new AbortController();
		setIsFetching(true);
		try {
			await fetchStreaming({
				url: `${deriveHost()}/gateway/api/assist/api/ai/v2/ai-feature/editor/suggested-edits/stream`,
				fetchOptions: {
					body: JSON.stringify({
						ai_feature_input: {
							page_url: '',
							page_adf: adf,
							suggestion_types: ['CONTENT_ACCURACY', 'PAGE_STRUCTURE', 'WRITING_STYLE'],
						},
					}),
				},
				signal: abortController.signal,
				onFinalResponse: (data) => {
					const suggestions = JSON.parse(data?.message?.content || '')?.suggestions;
					if (suggestions) {
						setSuggestions(suggestions);
					} else {
						// eslint-disable-next-line no-console
						console.error('No tool calls found in the response', data);
					}
				},
				onError: (error) => {
					// eslint-disable-next-line no-console
					console.error('Streaming error:', error);
				},
			});
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Failed to fetch suggestions:', error);
		} finally {
			setIsFetching(false);
		}
	}, [adf]);

	return (
		<Modal onClose={onClose} width="100%" height="100%" testId="suggestions-staging-area-modal">
			{globalStyles()}
			<StagingArea
				adf={adf}
				api={api}
				isFetching={isFetching}
				onClose={onClose}
				onFetchSuggestions={onFetchSuggestions}
				providerFactory={providerFactory}
				suggestions={suggestions}
			/>
		</Modal>
	);
}
